import { Container } from "basis";
import { StatementBanner } from "components/CreditCardAccount/Banner";
import { StatementPreferencesResponse } from "components/StatementPreference/response/response";
import { Account } from "library/accounts/types";
import { getHref } from "library/api";
import { useConfigFeature } from "library/hooks/useConfig";
import { useState } from "react";
import { useEventTracking } from "react-event-tracker";
import { useQuery } from "react-query";
import { Tab, TabList, TabPanel } from "react-tabs";
import { Layout } from "../Layout/Layout";
import { Tabs } from "../Tabs/Tabs";
import { AccountCard } from "./AccountCard";
import { Plans } from "./Plans/Plans";
import { Statements } from "./Statements/Statements";
import { Transactions } from "./Transactions/Transactions";

function getBannerText({
  pref,
  fee,
  fee_waive: feeWaived,
}: StatementPreferencesResponse): string {
  if (pref === "PAPER" && fee !== 0 && !feeWaived) {
    return `Every printed statement you receive costs you ${(
      fee / 100
    ).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    })}. So save up to ${((fee / 100) * 12).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    })} per year by switching to paperless.`;
  }
  if (pref === "PAPER") {
    return `Paper statements piling up? It’s easy to make the switch from paper to digital – and get your statements delivered online.`;
  }
  return "";
}

type Props = {
  location: {
    state: {
      account: Account;
      activeTab?: "statements";
    };
  };
};

export const CreditCardAccount = ({ location }: Props) => {
  const eStatementsActive = useConfigFeature("eStatements");
  const { state } = location;
  const { account, activeTab } = state;
  const plansHref = account.links.find(({ rel }) => rel === "plans")?.href;
  const hasPlans = plansHref !== undefined;
  const statementsHref = account.links.find(
    ({ rel }) => rel === "statements",
  )?.href;
  const statementPreferencesHref = account.links.find(
    ({ rel }) => rel === "statement-preferences",
  )?.href;
  const showStatements = eStatementsActive && statementsHref !== undefined;
  const defaultIndex = activeTab === "statements" && showStatements ? 2 : 0;
  const [selectedTab, setSelectedTab] = useState(defaultIndex);
  const { data } = useQuery(
    ["statement-preferences", { href: statementPreferencesHref }],
    () => getHref<StatementPreferencesResponse>(statementPreferencesHref),
    { enabled: statementPreferencesHref !== undefined },
  );
  const bannerText = data ? getBannerText(data.data) : null;
  const { trackEvent } = useEventTracking();

  return (
    <Layout>
      <Layout.Header>Account details</Layout.Header>
      <Layout.Main>
        <AccountCard
          {...account.credit_card}
          payin={account.payin}
          accountId={account.account_id}
          productId={account.product_id}
          accountNumber={account.account_number}
        />
        <Container margin="5 0 0 0">
          <Tabs
            defaultIndex={defaultIndex}
            onSelect={(index) => setSelectedTab(index)}
          >
            <TabList>
              <Tab
                onClick={(e) => {
                  trackEvent({
                    event: {
                      category: "tab",
                      action: "in-page-navigation",
                      location: "Account details",
                      label: e.target.innerText,
                    },
                  });
                }}
              >
                Transactions
              </Tab>
              {hasPlans && (
                <Tab
                  onClick={(e) => {
                    trackEvent({
                      event: {
                        category: "tab",
                        action: "in-page-navigation",
                        location: "Account details",
                        label: e.target.innerText,
                      },
                    });
                  }}
                >
                  Interest free
                </Tab>
              )}
              {showStatements && (
                <Tab
                  onClick={(e) => {
                    trackEvent({
                      event: {
                        category: "tab",
                        action: "in-page-navigation",
                        location: "Account details",
                        label: e.target.innerText,
                      },
                    });
                  }}
                >
                  Statements
                </Tab>
              )}
            </TabList>
            <Container padding="7 0">
              <TabPanel>
                <Transactions
                  accountId={account.account_id}
                  productId={account.product_id}
                />
              </TabPanel>
              {plansHref && (
                <TabPanel>
                  <Plans plansHref={plansHref} productId={account.product_id} />
                </TabPanel>
              )}
              {statementsHref && (
                <TabPanel>
                  <Statements href={statementsHref} />
                </TabPanel>
              )}
            </Container>
          </Tabs>
        </Container>
      </Layout.Main>
      {showStatements && selectedTab === (hasPlans ? 2 : 1) && bannerText && (
        <Layout.Sidebar>
          <StatementBanner text={bannerText} account={account} />
        </Layout.Sidebar>
      )}
    </Layout>
  );
};
